import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { LogOut, Users, Building2, Image, Car, Handshake, Trash2, Plus, Save, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const F = "w-full bg-hp-dark border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-hp-yellow";
const L = "block text-[10px] font-bold tracking-widest uppercase text-neutral-500 mb-1";
const STATUSES = ["nouveau", "contacté", "converti", "perdu"];

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const load = () => api.get("/admin/leads").then(({ data }) => setLeads(data));
  useEffect(() => { load(); }, []);
  const update = async (id, status) => { await api.patch(`/admin/leads/${id}`, { status }); load(); };
  const del = async (id) => { await api.delete(`/admin/leads/${id}`); load(); toast.success("Lead supprimé"); };
  return (
    <div className="space-y-3" data-testid="admin-leads">
      {leads.length === 0 && <p className="text-neutral-500">Aucun lead pour le moment.</p>}
      {leads.map((l) => (
        <div key={l.id} className="hp-card p-4 grid md:grid-cols-[1fr_auto] gap-3 items-start">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="bg-hp-yellow text-black text-[10px] font-bold uppercase px-2 py-0.5">{l.request_type}</span>
              <span className="font-display font-bold">{l.first_name} {l.last_name}</span>
              <span className="text-neutral-500 text-xs">{new Date(l.created_at).toLocaleString("fr-FR")}</span>
            </div>
            <p className="text-sm text-neutral-400 mt-1">{l.phone} · {l.email} · {l.centre_slug || "—"}</p>
            {l.vehicle_summary && <p className="text-sm text-neutral-300 mt-1">🚗 {l.vehicle_summary}</p>}
            {(l.service || l.plate) && <p className="text-xs text-neutral-500 mt-1">{[l.service, l.plate].filter(Boolean).join(" · ")}</p>}
            {l.message && <p className="text-sm text-neutral-400 mt-1 italic">"{l.message}"</p>}
          </div>
          <div className="flex items-center gap-2">
            <select className={F} value={l.status} onChange={(e) => update(l.id, e.target.value)} data-testid={`lead-status-${l.id}`}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={() => del(l.id)} className="text-neutral-500 hover:text-red-400 p-2" data-testid={`lead-delete-${l.id}`}><Trash2 size={16} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function FranchiseTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/franchise").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="admin-franchise">
      {items.length === 0 && <p className="text-neutral-500">Aucune demande de franchise.</p>}
      {items.map((x) => (
        <div key={x.id} className="hp-card p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display font-bold">{x.first_name} {x.last_name}</span>
            <span className="text-neutral-500 text-xs">{new Date(x.created_at).toLocaleString("fr-FR")}</span>
          </div>
          <p className="text-sm text-neutral-400 mt-1">{x.phone} · {x.email} · {x.city_dept}</p>
          <p className="text-xs text-neutral-500 mt-1">Société: {x.company || "—"} · Activité: {x.activity || "—"} · Garage: {x.has_garage ? "Oui" : "Non"} · Banc: {x.has_dyno ? "Oui" : "Non"}</p>
          {x.message && <p className="text-sm text-neutral-400 mt-1 italic">"{x.message}"</p>}
        </div>
      ))}
    </div>
  );
}

function CentresTab() {
  const [centres, setCentres] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/centres").then(({ data }) => setCentres(data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const { slug, ...body } = edit;
    await api.put(`/admin/centres/${slug}`, body);
    toast.success("Centre mis à jour"); setEdit(null); load();
  };
  const fields = [["name", "Nom"], ["city", "Ville"], ["tagline", "Slogan"], ["address", "Adresse"], ["postal_city", "CP + Ville"], ["phone", "Téléphone"], ["email", "Email"], ["hours", "Horaires"], ["maps_url", "Google Maps URL"], ["instagram", "Instagram"], ["google_reviews_url", "Avis Google URL"], ["image", "Image URL"]];
  return (
    <div className="space-y-3" data-testid="admin-centres">
      {centres.map((c) => (
        <div key={c.slug} className="hp-card p-4">
          <div className="flex items-center justify-between">
            <span className="font-display font-bold uppercase">{c.name}{c.is_mother ? " · Maison mère" : ""}</span>
            <button onClick={() => setEdit(edit?.slug === c.slug ? null : { ...c })} className="hp-ghost text-xs px-3 py-1.5" data-testid={`centre-edit-${c.slug}`}>{edit?.slug === c.slug ? "Fermer" : "Modifier"}</button>
          </div>
          {edit?.slug === c.slug && (
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {fields.map(([k, lbl]) => (
                <div key={k} className={k === "image" || k === "maps_url" ? "sm:col-span-2" : ""}>
                  <label className={L}>{lbl}</label>
                  <input className={F} value={edit[k] || ""} onChange={(e) => setEdit((s) => ({ ...s, [k]: e.target.value }))} data-testid={`centre-field-${k}`} />
                </div>
              ))}
              <div><label className={L}>Note Google</label><input type="number" step="0.1" className={F} value={edit.google_rating || ""} onChange={(e) => setEdit((s) => ({ ...s, google_rating: e.target.value ? +e.target.value : null }))} /></div>
              <div><label className={L}>Nb avis Google</label><input type="number" className={F} value={edit.google_reviews_count || ""} onChange={(e) => setEdit((s) => ({ ...s, google_reviews_count: e.target.value ? +e.target.value : null }))} /></div>
              <div className="sm:col-span-2"><label className={L}>Description</label><textarea rows={3} className={F} value={edit.description || ""} onChange={(e) => setEdit((s) => ({ ...s, description: e.target.value }))} /></div>
              <button onClick={save} className="hp-cta px-5 py-2.5 text-sm inline-flex items-center gap-2 sm:col-span-2" data-testid={`centre-save-${c.slug}`}><Save size={15} /> Enregistrer</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const EMPTY_REAL = { title: "", category: "STAGE 1", tags: ["AUTO", "STAGE 1"], image: "", vehicle: "", engine: "", origin_hp: 0, final_hp: 0, origin_nm: 0, final_nm: 0, modification: "", centre_slug: "", order: 0 };
const CATS = ["AUTO", "MOTO", "STAGE 1", "STAGE 2", "STAGE 3", "E85", "BANC"];

function RealisationsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/admin/realisations").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    const body = { ...form, tags: [form.category] };
    if (form.id) await api.put(`/admin/realisations/${form.id}`, body);
    else await api.post("/admin/realisations", body);
    toast.success("Réalisation enregistrée"); setForm(null); load();
  };
  const del = async (id) => { await api.delete(`/admin/realisations/${id}`); load(); toast.success("Supprimée"); };
  return (
    <div data-testid="admin-realisations">
      <button onClick={() => setForm({ ...EMPTY_REAL })} className="hp-cta px-4 py-2.5 text-sm inline-flex items-center gap-2 mb-4" data-testid="real-add"><Plus size={16} /> Ajouter une réalisation</button>
      {form && (
        <div className="hp-card p-5 mb-5 grid sm:grid-cols-2 gap-3">
          <div><label className={L}>Titre</label><input className={F} value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="real-title" /></div>
          <div><label className={L}>Catégorie</label><select className={F} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="real-category">{CATS.map((c) => <option key={c}>{c}</option>)}</select></div>
          <div><label className={L}>Véhicule</label><input className={F} value={form.vehicle} onChange={(e) => set("vehicle", e.target.value)} /></div>
          <div><label className={L}>Motorisation</label><input className={F} value={form.engine} onChange={(e) => set("engine", e.target.value)} /></div>
          <div><label className={L}>CH origine</label><input type="number" className={F} value={form.origin_hp} onChange={(e) => set("origin_hp", +e.target.value)} /></div>
          <div><label className={L}>CH final</label><input type="number" className={F} value={form.final_hp} onChange={(e) => set("final_hp", +e.target.value)} /></div>
          <div><label className={L}>NM origine</label><input type="number" className={F} value={form.origin_nm} onChange={(e) => set("origin_nm", +e.target.value)} /></div>
          <div><label className={L}>NM final</label><input type="number" className={F} value={form.final_nm} onChange={(e) => set("final_nm", +e.target.value)} /></div>
          <div><label className={L}>Modification</label><input className={F} value={form.modification} onChange={(e) => set("modification", e.target.value)} /></div>
          <div><label className={L}>Centre (slug)</label><input className={F} value={form.centre_slug} onChange={(e) => set("centre_slug", e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={L}>Image URL</label><input className={F} value={form.image} onChange={(e) => set("image", e.target.value)} data-testid="real-image" /></div>
          <div className="sm:col-span-2 flex gap-3">
            <button onClick={save} className="hp-cta px-5 py-2.5 text-sm inline-flex items-center gap-2" data-testid="real-save"><Save size={15} /> Enregistrer</button>
            <button onClick={() => setForm(null)} className="hp-ghost px-5 py-2.5 text-sm">Annuler</button>
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((r) => (
          <div key={r.id} className="hp-card overflow-hidden">
            {r.image && <img src={r.image} alt={r.title} className="h-32 w-full object-cover" />}
            <div className="p-3">
              <p className="font-display font-bold text-sm">{r.vehicle}</p>
              <p className="text-xs text-neutral-500">{r.category} · {r.origin_hp}→{r.final_hp} CH</p>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setForm({ ...r })} className="hp-ghost text-xs px-3 py-1.5 flex-1">Modifier</button>
                <button onClick={() => del(r.id)} className="text-neutral-500 hover:text-red-400 px-2"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY_V = { vtype: "Voiture", brand: "", model: "", generation: "", engine: "", fuel: "Essence", origin_hp: null, origin_nm: null, stage1_hp: null, stage1_nm: null, stage2_hp: null, stage2_nm: null, e85_hp: null, e85_nm: null, e85_available: false };

function CatalogTab() {
  const [form, setForm] = useState({ ...EMPTY_V });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const num = (k, v) => set(k, v === "" ? null : +v);
  const save = async () => {
    if (!form.brand || !form.model || !form.engine) return toast.error("Marque, modèle et motorisation requis.");
    await api.post("/admin/catalog", form);
    toast.success("Véhicule ajouté au catalogue"); setForm({ ...EMPTY_V });
  };
  const fields = [["brand", "Marque"], ["model", "Modèle"], ["generation", "Génération/Version"], ["engine", "Motorisation"], ["fuel", "Carburant"]];
  const nums = [["origin_hp", "CH origine"], ["origin_nm", "NM origine"], ["stage1_hp", "CH Stage 1"], ["stage1_nm", "NM Stage 1"], ["stage2_hp", "CH Stage 2"], ["stage2_nm", "NM Stage 2"], ["e85_hp", "CH E85"], ["e85_nm", "NM E85"]];
  return (
    <div data-testid="admin-catalog">
      <p className="text-neutral-400 text-sm mb-4">Ajoutez un véhicule au configurateur. Renseignez uniquement les stages disponibles pour ce véhicule.</p>
      <div className="hp-card p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {fields.map(([k, lbl]) => (<div key={k}><label className={L}>{lbl}</label><input className={F} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`cat-${k}`} /></div>))}
        {nums.map(([k, lbl]) => (<div key={k}><label className={L}>{lbl}</label><input type="number" className={F} value={form[k] ?? ""} onChange={(e) => num(k, e.target.value)} /></div>))}
        <label className="flex items-center gap-2 text-sm text-neutral-300 self-end"><input type="checkbox" checked={form.e85_available} onChange={(e) => set("e85_available", e.target.checked)} className="h-4 w-4 accent-[#FFED00]" /> E85 disponible</label>
        <button onClick={save} className="hp-cta px-5 py-2.5 text-sm inline-flex items-center gap-2 lg:col-span-3" data-testid="cat-save"><Plus size={15} /> Ajouter le véhicule</button>
      </div>
    </div>
  );
}

const FULFILL = { pending: "À préparer", ready: "Prête (retrait)", shipped: "Expédiée", delivered: "Remise / livrée" };
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, []);
  const update = async (id, fulfillment) => { await api.patch(`/admin/orders/${id}`, { fulfillment }); load(); };
  const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
  return (
    <div className="space-y-3" data-testid="admin-orders">
      {orders.length === 0 && <p className="text-neutral-500">Aucune commande pour le moment.</p>}
      {orders.map((o) => (
        <div key={o.order_id} className="hp-card p-4 grid md:grid-cols-[1fr_auto] gap-3 items-start" data-testid={`order-${o.order_id}`}>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${o.payment_status === "paid" ? "bg-hp-yellow text-black" : "bg-white/10 text-neutral-300"}`}>{o.payment_status === "paid" ? "Payée" : o.payment_status}</span>
              <span className="font-display font-bold">#{o.order_id}</span>
              <span className="font-display">{eur(o.total)}</span>
              <span className="text-neutral-500 text-xs">{new Date(o.created_at).toLocaleString("fr-FR")}</span>
            </div>
            <p className="text-sm text-neutral-400 mt-1">{o.customer.first_name} {o.customer.last_name} · {o.customer.phone} · {o.customer.email}</p>
            <p className="text-xs text-neutral-500 mt-1">{o.delivery === "pickup" ? `Retrait — ${o.centre_name}` : `Livraison — ${o.customer.address}, ${o.customer.postal_code} ${o.customer.city}`}</p>
            <ul className="text-sm text-neutral-300 mt-2">{o.items.map((i, k) => <li key={k}>• {i.name} {Object.values(i.options).join(" / ")} × {i.qty}</li>)}</ul>
          </div>
          {o.payment_status === "paid" && (
            <select className={F} value={o.fulfillment || "pending"} onChange={(e) => update(o.order_id, e.target.value)} data-testid={`order-fulfill-${o.order_id}`}>
              {Object.entries(FULFILL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          )}
        </div>
      ))}
    </div>
  );
}

const TABS = [
  { id: "leads", label: "Leads", icon: Users, comp: LeadsTab },
  { id: "orders", label: "Commandes", icon: ShoppingBag, comp: OrdersTab },
  { id: "franchise", label: "Franchise", icon: Handshake, comp: FranchiseTab },
  { id: "centres", label: "Centres", icon: Building2, comp: CentresTab },
  { id: "realisations", label: "Réalisations", icon: Image, comp: RealisationsTab },
  { id: "catalog", label: "Catalogue", icon: Car, comp: CatalogTab },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);

  useEffect(() => { if (user) api.get("/admin/stats").then(({ data }) => setStats(data)); }, [user, tab]);

  if (user === null) return <div className="min-h-screen grid place-items-center text-neutral-500">Chargement…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const Active = TABS.find((t) => t.id === tab).comp;

  return (
    <div className="min-h-screen bg-carbon-grid" data-testid="admin-dashboard">
      <header className="sticky top-0 z-20 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/assets/logo-horsepower.png" alt="Horsepower Garage" className="h-8 w-auto" />
            <span className="font-display font-black uppercase tracking-tight text-hp-yellow text-sm">Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-neutral-400 hidden sm:inline">{user.email}</span>
            <button onClick={logout} className="hp-ghost text-xs px-3 py-2 inline-flex items-center gap-1.5" data-testid="admin-logout"><LogOut size={14} /> Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            {[["Leads", stats.leads], ["Nouveaux", stats.leads_new], ["Franchise", stats.franchise], ["Centres", stats.centres], ["Réalisations", stats.realisations], ["Véhicules", stats.vehicles]].map(([l, n]) => (
              <div key={l} className="hp-card p-4"><p className="font-stat font-black text-3xl text-hp-yellow">{n}</p><p className="text-[10px] uppercase tracking-widest text-neutral-500">{l}</p></div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-3">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2 text-sm font-bold uppercase tracking-wide inline-flex items-center gap-2 transition-colors ${tab === t.id ? "bg-hp-yellow text-black" : "text-neutral-300 hover:text-hp-yellow"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>
        <Active />
      </div>
    </div>
  );
}
